import React from "react";
import { useNavigate } from "react-router-dom";
import { apiTrustsIndex, TrustEntity } from "../lib/api";

export default function TrustDashboard() {
  const nav = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [trusts, setTrusts] = React.useState<TrustEntity[]>([]);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiTrustsIndex();
        if (!alive) return;
        setTrusts(res.trusts || []);
      } catch (e: any) {
        if (!alive) return;
        setError(e?.message || "Failed to load trusts.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const hasAny = trusts.length > 0;
  const latest = hasAny ? trusts[0] : null;

  const latestStatus = (latest?.status || "").toLowerCase();
  const canApply = !hasAny;
  const canReapply = !!latest && latestStatus === "rejected";

  function statusClasses(status: string): string {
    const s = status.toLowerCase();
    if (s === "rejected") return "bg-red-100 text-red-700";
    if (s === "active" || s === "approved") return "bg-green-100 text-green-800";
    if (s === "pending" || s === "submitted" || s === "processing")
      return "bg-yellow-100 text-yellow-800";
    if (s === "terminated") return "bg-gray-200 text-gray-600";
    return "bg-gray-100 text-gray-700";
  }

  return (
    <div className="section-box max-w-md mx-auto p-4 sm:p-6 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h1 className="text-lg font-semibold">Your Trusts</h1>
          <p className="text-xs text-gray-500 mt-1">
            View the status of your trust structure and access your deed.
          </p>
        </div>

        {canApply && (
          <button
            type="button"
            onClick={() => nav("/trust/apply")}
            className="btn btn-success text-sm px-4 py-2"
          >
            Apply
          </button>
        )}

        {canReapply && latest && (
          <button
            type="button"
            onClick={() => nav(`/trusts/${latest.id}/reapply`)}
            className="btn btn-success text-sm px-4 py-2"
          >
            Reapply
          </button>
        )}
      </div>

      {/* Loading / error states */}
      {loading && <div className="skeleton h-24" />}

      {!loading && error && (
        <div className="alert-danger text-sm mb-4">{error}</div>
      )}

      {/* Empty state */}
      {!loading && !error && !hasAny && (
        <div className="card-box p-4 text-sm text-gray-600">
          <p>You have not created a trust application yet.</p>
          <p className="mt-1">
            Tap <span className="font-medium">Apply</span> above to start your trust
            onboarding.
          </p>
        </div>
      )}

      {/* Trust list */}
      {!loading && !error && hasAny && (
        <div className="space-y-4">
          {trusts.map((t) => {
            const status = (t.status || "").toLowerCase();
            const isRejected = status === "rejected";
            const jurisdictionLabel = t.jurisdiction || t.jurisdiction_code || "—";

            return (
              <div key={t.id} className="card-box p-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">#{t.id}</div>
                  <span
                    className={
                      "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium " +
                      statusClasses(status)
                    }
                  >
                    {status || "draft"}
                  </span>
                </div>

                <div className="mt-1">
                  <div className="font-medium text-sm">
                    {t.trust_name || "Untitled trust"}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {jurisdictionLabel}
                  </div>
                </div>

                {isRejected && t.rejection_reason && (
                  <div className="mt-2 rounded-md bg-red-50 border border-red-100 px-3 py-2">
                    <p className="text-[11px] font-semibold text-red-700">
                      Rejection reason from admin
                    </p>
                    <p className="text-[11px] text-red-700 mt-1 whitespace-pre-line">
                      {t.rejection_reason}
                    </p>
                  </div>
                )}

                <div className="mt-3 flex justify-between items-center">
                  <button
                    type="button"
                    onClick={() => nav(`/trusts/${t.id}`)}
                    className="btn btn-white text-xs px-4 py-2"
                  >
                    Open
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
